import { lazy, type ReactNode, Suspense } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import { AppShell } from "./components/AppShell";
import { ConnectFailed } from "./components/ConnectFailed";
import { Loading } from "./components/ui";
import { useAuth } from "./lib/auth";

const page = (load: () => Promise<{ default: React.ComponentType }>) => {
  const C = lazy(load);
  return (
    <Suspense fallback={<Loading full />}>
      <C />
    </Suspense>
  );
};

/** 入るまで待つ。入れなければ（503・つながらない）接続できない画面と再試行だけ（ログインの画面は無い） */
function RequireAuth({ children }: { children: ReactNode }) {
  const { state, retry } = useAuth();
  if (state.status === "loading") return <Loading full />;
  if (state.status === "failed") return <ConnectFailed reason={state.reason} onRetry={retry} />;
  return <>{children}</>;
}

/** 登録とログインを消す前の URL（ブックマーク・メールのリンク）はホームへ */
export const OLD_PATHS = ["/login", "/signup", "/signup/confirm", "/forgot", "/new-password", "/account/password", "/settings/users"];

export const routes = [
  ...OLD_PATHS.map((path) => ({ path, element: <Navigate to="/" replace /> })),
  {
    path: "/proposals/:id/revisions/:revId/print",
    element: <RequireAuth>{page(() => import("./pages/Print"))}</RequireAuth>,
  },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { path: "/", element: page(() => import("./pages/ProposalList")) },
      { path: "/proposals/new", element: page(() => import("./pages/ProposalNew")) },
      { path: "/proposals/:id", element: page(() => import("./pages/ProposalDetail")) },
      { path: "/proposals/:id/edit", element: page(() => import("./editor/LayoutEditor")) },
      { path: "/proposals/:id/revisions/:revId/estimate", element: page(() => import("./pages/Estimate")) },
      { path: "/customers", element: page(() => import("./pages/Customers")) },
      { path: "/customers/:id", element: page(() => import("./pages/CustomerDetail")) },
      { path: "/products", element: page(() => import("./pages/Products")) },
      { path: "/products/new", element: page(() => import("./pages/ProductEdit")) },
      { path: "/products/:id", element: page(() => import("./pages/ProductEdit")) },
      { path: "/processes", element: page(() => import("./pages/Processes")) },
      { path: "/patterns", element: page(() => import("./pages/Patterns")) },
      { path: "/patterns/new", element: page(() => import("./pages/PatternEdit")) },
      { path: "/patterns/:id", element: page(() => import("./pages/PatternEdit")) },
      { path: "/imports", element: page(() => import("./pages/Imports")) },
      { path: "/imports/:id", element: page(() => import("./pages/ImportCheck")) },
      { path: "/settings", element: <Navigate to="/settings/rules" replace /> },
      { path: "/settings/rules", element: page(() => import("./pages/settings/Rules")) },
      { path: "/settings/ai", element: page(() => import("./pages/settings/Ai")) },
      { path: "*", element: page(() => import("./pages/NotFound")) },
    ],
  },
];

const router = createBrowserRouter(routes);

export function App() {
  return <RouterProvider router={router} />;
}
